import { Box, DialogContent, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import Search from '@hapana/shared/web/components/shared/search/search.component'
import TablePagination from '@hapana/shared/web/components/shared/table/components/table-pagination.component'
import { useListEquipments, useEquipmentDiagnostics } from '_hooks/equipments.hooks.js'
import { useSiteEnableTanningFeatures } from '_hooks/sites.hooks'
import useNow from '_hooks/use-now.js'
import useSiteStore from '_stores/site.store'

import { EquipmentRow } from './equipment-row/equipment-row.component'
import { PAGE_SIZE_OPTIONS } from './manage-equipments.constants'
import { styles } from './manage-equipments-tab.styles.js'

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 10

const ManageEquipmentsTab = () => {
  const { t } = useTranslation(['checkIn'])
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')

  const { data, isLoading, isError } = useListEquipments(siteId, {
    last: true,
    search: search || undefined,
  })
  const { data: tanningFeatures } = useSiteEnableTanningFeatures()
  const hasExternalAgent = !!tanningFeatures?.integrations?.tanning?.externalApiUrl
  const { data: diagnostics } = useEquipmentDiagnostics(siteId, {
    enabled: !!siteId && hasExternalAgent,
  })
  const now = useNow()

  const equipments = data?.equipments ?? []
  const pagedEquipments = equipments.slice(page * pageSize, (page + 1) * pageSize)

  const handleSearch = useCallback((value) => {
    setSearch(value)
    setPage(0)
  }, [])

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize)
    setPage(0)
  }, [])

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={styles.stateContainer}>
          <Loading />
        </Box>
      )
    }

    if (isError) {
      return (
        <Box sx={styles.stateContainer}>
          <Typography typography="body" color="gray.56">
            {t('checkIn:equipments.manageEquipments.error')}
          </Typography>
        </Box>
      )
    }

    if (equipments.length === 0) {
      return (
        <Box sx={styles.stateContainer}>
          <Typography typography="body" color="gray.56">
            {search
              ? t('checkIn:emptySearchResult')
              : t('checkIn:equipments.manageEquipments.noEquipments')}
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={styles.tableSection}>
        <Box sx={styles.tableWrapper}>
          {pagedEquipments.map((equipment) => (
            <EquipmentRow
              key={equipment.equipmentId}
              equipment={equipment}
              siteId={siteId}
              diagnostics={diagnostics}
              now={now}
            />
          ))}
        </Box>
        <TablePagination
          page={page}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          canGoBack={page > 0}
          canGoForward={(page + 1) * pageSize < equipments.length}
          onNextPage={() => setPage((prev) => prev + 1)}
          onPreviousPage={() => setPage((prev) => prev - 1)}
          currentPageRecords={pagedEquipments.length}
          totalResultsQuantity={equipments.length}
          isLoading={isLoading}
        />
      </Box>
    )
  }

  return (
    <DialogContent sx={styles.dialogContent}>
      <Search
        placeholder={t('checkIn:equipments.manageEquipments.searchEquipment')}
        searchCallback={handleSearch}
        debounceValue={SEARCH_DEBOUNCE_MS}
        showSearchFieldBorder={false}
        sx={styles.search}
      />
      <Box sx={styles.bandDivider} />
      {renderContent()}
    </DialogContent>
  )
}

export default ManageEquipmentsTab
